import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { getSupabaseBrowserClient, getSupabaseCurrentUser } from "@/lib/supabase";
import type { User } from "@supabase/supabase-js";

interface AuthState {
  user: User | null;
  loading: boolean;
}

export function useAuth(): AuthState & { signOut: () => Promise<void> } {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });
  const navigate = useNavigate();
  const resolvedRef = useRef(false);

  useEffect(() => {
    let active = true;
    resolvedRef.current = false;
    const supabase = getSupabaseBrowserClient();

    getSupabaseCurrentUser()
      .then((user) => {
        if (active && !resolvedRef.current) {
          resolvedRef.current = true;
          setState({ user, loading: false });
        }
      })
      .catch((error) => {
        Sentry.logger.warn("useAuth getUser failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        if (active && !resolvedRef.current) {
          resolvedRef.current = true;
          setState({ user: null, loading: false });
        }
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      resolvedRef.current = true;
      setState({ user: session?.user ?? null, loading: false });
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseBrowserClient();
    await supabase.auth.signOut();
    navigate({ to: "/", replace: true });
  }, [navigate]);

  return { ...state, signOut };
}
